/**
 * faqService.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * [배경/목적]
 * FAQ CRUD 기능을 위한 Firestore 서비스
 * 'faqs' 컬렉션에 대한 생성, 조회, 수정, 삭제 작업 수행
 * 
 * [Firestore 구조]
 * - 컬렉션: faqs
 * - 필드: { id, question, answer, order, isActive, created_at, updated_at }
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { FAQ, FAQInput, FAQUpdateInput, FAQFilterOptions } from '../types/faq.types';

// Firestore 컬렉션 참조
const FAQ_COLLECTION = 'faqs';
const faqsRef = collection(db, FAQ_COLLECTION);

/**
 * 모든 FAQ 목록 조회
 * @param options - 필터 옵션 (activeOnly, sortOrder)
 * @returns FAQ 배열
 * 
 * [참고] Firestore 복합 인덱스 없이도 작동하도록 클라이언트 측 필터링 사용
 */
export async function getAllFAQs(options: FAQFilterOptions = {}): Promise<FAQ[]> {
  const { activeOnly = false, sortOrder = 'asc' } = options;

  try {
    // 모든 FAQ 조회 (인덱스 불필요)
    const snapshot = await getDocs(faqsRef);
    
    let faqs: FAQ[] = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as FAQ[];

    // 클라이언트 측에서 활성화된 FAQ만 필터링
    if (activeOnly) {
      faqs = faqs.filter((faq) => faq.isActive === true);
    }

    // 클라이언트 측에서 order 기준 정렬
    faqs.sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
    });

    return faqs;
  } catch (error) {
    console.error('[faqService] getAllFAQs 실패:', error);
    throw error;
  }
}

/**
 * 단일 FAQ 조회
 * @param id - FAQ 문서 ID
 * @returns FAQ 또는 null
 */
export async function getFAQById(id: string): Promise<FAQ | null> {
  try {
    const docRef = doc(db, FAQ_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as FAQ;
  } catch (error) {
    console.error('[faqService] getFAQById 실패:', error);
    throw error;
  }
}

/**
 * FAQ 생성
 * @param input - FAQ 입력 데이터
 * @returns 생성된 FAQ
 */
export async function createFAQ(input: FAQInput): Promise<FAQ> {
  try {
    const now = Timestamp.now();
    const newFAQ = {
      question: input.question,
      answer: input.answer,
      order: input.order,
      isActive: input.isActive ?? true,
      created_at: now,
      updated_at: now,
    };

    const docRef = await addDoc(faqsRef, newFAQ);

    return {
      id: docRef.id,
      ...newFAQ,
    };
  } catch (error) {
    console.error('[faqService] createFAQ 실패:', error);
    throw error;
  }
}

/**
 * FAQ 수정
 * @param id - FAQ 문서 ID
 * @param input - 수정할 데이터 (부분 업데이트)
 * @returns 수정된 FAQ
 */
export async function updateFAQ(id: string, input: FAQUpdateInput): Promise<FAQ> {
  try {
    const docRef = doc(db, FAQ_COLLECTION, id);
    const updateData = {
      ...input,
      updated_at: Timestamp.now(),
    };

    await updateDoc(docRef, updateData);

    // 수정된 문서 재조회
    const updatedDoc = await getDoc(docRef);
    return {
      id: updatedDoc.id,
      ...updatedDoc.data(),
    } as FAQ;
  } catch (error) {
    console.error('[faqService] updateFAQ 실패:', error);
    throw error;
  }
}

/**
 * FAQ 삭제
 * @param id - FAQ 문서 ID
 */
export async function deleteFAQ(id: string): Promise<void> {
  try {
    const docRef = doc(db, FAQ_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('[faqService] deleteFAQ 실패:', error);
    throw error;
  }
}

/**
 * 여러 FAQ의 순서 일괄 업데이트
 * @param updates - { id, order } 배열
 */
export async function updateFAQOrders(updates: { id: string; order: number }[]): Promise<void> {
  try {
    const batch = writeBatch(db);
    const now = Timestamp.now();

    updates.forEach(({ id, order }) => {
      const docRef = doc(db, FAQ_COLLECTION, id);
      batch.update(docRef, { order, updated_at: now });
    });

    await batch.commit();
  } catch (error) {
    console.error('[faqService] updateFAQOrders 실패:', error);
    throw error;
  }
}

/**
 * FAQ 활성화 상태 토글
 * @param id - FAQ 문서 ID
 * @param isActive - 활성화 여부
 */
export async function toggleFAQActive(id: string, isActive: boolean): Promise<void> {
  try {
    const docRef = doc(db, FAQ_COLLECTION, id);
    await updateDoc(docRef, {
      isActive,
      updated_at: Timestamp.now(),
    });
  } catch (error) {
    console.error('[faqService] toggleFAQActive 실패:', error);
    throw error;
  }
}

/**
 * 다음 순서 번호 가져오기
 * @returns 다음 order 값
 */
export async function getNextOrder(): Promise<number> {
  try {
    const q = query(faqsRef, orderBy('order', 'desc'));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return 1;
    }

    const maxOrder = snapshot.docs[0].data().order || 0;
    return maxOrder + 1;
  } catch (error) {
    console.error('[faqService] getNextOrder 실패:', error);
    return 1;
  }
}
